export const CAMPAIGN_STATUS = {
  AVAILABLE: "AVAILABLE",
  RESERVED: "RESERVED",
  REDEEMED: "REDEEMED",
  EXPIRED: "EXPIRED",
};

export const RESERVATION_WINDOW_MINUTES = 2;
export const RESERVATION_WINDOW_MS = RESERVATION_WINDOW_MINUTES * 60 * 1000;

export const normalizeStatus = (value) => {
  const status = String(value || CAMPAIGN_STATUS.AVAILABLE).toUpperCase();
  if (status === "ALLOCATED" || status === "BOOKED") return CAMPAIGN_STATUS.REDEEMED;
  if (status === "ACTIVE") return CAMPAIGN_STATUS.AVAILABLE;
  if (Object.values(CAMPAIGN_STATUS).includes(status)) return status;
  return CAMPAIGN_STATUS.AVAILABLE;
};

const isEnabledFlag = (value) => value === 1 || value === true || String(value) === "1";

export const getCampaignUuid = (campaign = {}) =>
  campaign.uuid || campaign.campaign_uuid || campaign.id;

export const getCampaignTitle = (campaign = {}) =>
  campaign.title || campaign.name || campaign.campaign_name || "Campaign";

export const getCampaignDescription = (campaign = {}) =>
  campaign.description ||
  campaign.short_description ||
  campaign.summary ||
  "Book your ticket and complete payment before the timer expires.";

export const getCampaignImage = (campaign = {}) =>
  campaign.image_url ||
  campaign.image ||
  campaign.banner_url ||
  campaign.banner ||
  campaign.thumbnail_url ||
  campaign.thumbnail ||
  "";

export const getCampaignStatus = (campaign = {}) => {
  const bookedFlag = campaign.booked ?? campaign.Booked ?? campaign.is_booked ?? campaign.isBooked;
  const reservedFlag =
    campaign.reserved ?? campaign.Reserved ?? campaign.is_reserved ?? campaign.isReserved;
  const userTicketStatus =
    campaign.user_ticket?.status ||
    campaign.userTicket?.status ||
    campaign.booked_ticket?.status ||
    campaign.ticket?.status;

  if (isEnabledFlag(bookedFlag)) return CAMPAIGN_STATUS.REDEEMED;
  if (isEnabledFlag(reservedFlag)) return CAMPAIGN_STATUS.RESERVED;

  return normalizeStatus(
    userTicketStatus ||
      campaign.user_status ||
      campaign.ticket_status ||
      campaign.status ||
      campaign.reservation_status,
  );
};

export const getReservationExpiry = (campaign = {}) => {
  const expiry =
    campaign.reservation_expires_at ||
    campaign.reserved_until ||
    campaign.expires_at ||
    campaign.expiry_at;
  const reservedAt = campaign.reserved_at || campaign.reservedAt;

  if (!expiry || !reservedAt) return expiry;

  const expiryTime = new Date(expiry).getTime();
  const reservedTime = new Date(reservedAt).getTime();

  if (Number.isNaN(expiryTime) || Number.isNaN(reservedTime)) return expiry;

  return new Date(Math.min(expiryTime, reservedTime + RESERVATION_WINDOW_MS)).toISOString();
};

// City uuid the campaign is scoped to. Empty means the campaign runs everywhere.
export const getCampaignCityUuid = (campaign = {}) =>
  campaign.city || campaign.city_uuid || campaign.cities?.[0]?.uuid || "";

// Display name for that city, when the endpoint resolved it.
export const getCampaignCityName = (campaign = {}) =>
  campaign.cities?.[0]?.name || campaign.city_name || "";

export const getCampaignEndDate = (campaign = {}) =>
  campaign.ends_at || campaign.end_date || campaign.valid_until || campaign.expiry_date;

export const getCampaignEventDate = (campaign = {}) =>
  campaign.event_date || campaign.event_at || campaign.starts_at || campaign.start_date;

const resolveAvailability = (campaign = {}) => {
  const available =
    campaign.available ??
    campaign.availableTickets ??
    campaign.available_tickets_count ??
    campaign.available_tickets ??
    campaign.available_count ??
    campaign.availableTicketCount ??
    campaign.tickets_available ??
    campaign.ticket_available ??
    campaign.remaining_tickets ??
    campaign.remaining_count;
  const total =
    campaign.total ??
    campaign.totalTickets ??
    campaign.total_tickets ??
    campaign.total_ticket_count ??
    campaign.ticket_count ??
    campaign.total_count;
  return { available, total };
};

export const getAvailabilityText = (campaign = {}) => {
  const { available, total } = resolveAvailability(campaign);

  if (available == null && total == null) return "0 available";
  if (available != null && total != null) return `${available}/${total} available`;
  if (available != null) return `${available} available`;
  return `${total} total tickets`;
};

export const getAvailabilityCount = (campaign = {}) => {
  const { available, total } = resolveAvailability(campaign);

  if (available == null && total == null) return "0/0";
  if (available != null && total != null) return `${available}/${total}`;
  if (available != null) return `${available}`;
  return `${total}`;
};

export const formatCurrency = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "";
  return amount.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
  });
};

export const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export const getPaymentUrl = (payload = {}) =>
  payload.payment_url ||
  payload.checkout_url ||
  payload.redirect_url ||
  payload.url ||
  payload?.data?.payment_url ||
  payload?.data?.checkout_url ||
  payload?.data?.redirect_url ||
  payload?.data?.url;
